import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, RouterOutlet } from '@angular/router';
import { HeaderComponent } from '@shared/components/header/header.component';
import { NavItem } from '@shared/interfaces/nav/nav-item';
import { ConnectionPointDto } from '@app/api/nts';
import { formatAddress } from '@app/core/utils/format-address';
import { generateNavItems } from '@app/core/utils/generate-nav-items';
import { connectionpointNavItems } from '@views/connectionpoint/connectionpoint-details/connectionpoint-nav-items';
import { StateManagerService } from '@shared/state/state-manager.service';
import { HeaderService } from '@app/core/services/header.service';
import { TranslatePipe } from '@ngx-translate/core';
import { MapComponent } from '@shared/components/map/map.component';
import { ModalService } from '@app/core/services/modal.service';
import {
  ConnectionpointEditComponent
} from '@shared/modals/connectionpoint/connectionpoint-edit/connectionpoint-edit.component';
import { hasRole } from '@app/core/guards/has-role.guard';
import { AuthRoles } from '@app/api/dmc';
import { LucideAngularModule, Map, PencilLine } from 'lucide-angular';
import { MenuAction } from '@shared/interfaces/header/menu-action';

@Component({
  selector: 'app-connectionpoint-search-details',
  imports: [
    HeaderComponent,
    LucideAngularModule,
    RouterOutlet,
    TranslatePipe,
    MapComponent,
  ],
  templateUrl: './connectionpoint-details.component.html',
  styleUrl: './connectionpoint-details.component.css'
})
export class ConnectionpointDetailsComponent implements OnInit {
  public id!: string;
  public connectionPoint?: ConnectionPointDto;
  public address?: string;
  public navItems: NavItem[] = [];
  public mapVisible = false;

  private readonly cpWriter = hasRole([AuthRoles.ConnectionPointWriter]);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly stateManagerService: StateManagerService,
    private readonly headerService: HeaderService,
    private readonly modalService: ModalService,
  ) { }

  public ngOnInit(): void {
    this.route.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;

      this.stateManagerService.getConnectionPoint(this.id).subscribe((connectionPoint: ConnectionPointDto) => {
        this.connectionPoint = connectionPoint;
        this.address = connectionPoint?.address ? formatAddress(connectionPoint.address) : undefined;

        if (connectionPoint.identification) {
          this.headerService.setResource(connectionPoint.identification);
        }

        this.headerService.setTitle(this.address);
        this.navItems = generateNavItems(connectionpointNavItems, 'id', this.id);
        this.updateActions();
      });
    });
  }

  public toggleMap(): void {
    this.mapVisible = !this.mapVisible;
    this.updateActions();
  }

  public openEditModal(): void {
    this.modalService.show({
      component: ConnectionpointEditComponent,
      title: 'action.edit',
      actions: [
        { type: 'primary', label: 'system.button.edit', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { id: this.id, connectionPoint: this.connectionPoint }
    }).subscribe(() => {
      this.stateManagerService.getConnectionPoint(this.id).subscribe(cp => {
        this.connectionPoint = cp;
      });
    });
  }

  private updateActions(): void {
    const actions: MenuAction[] = [
      {
        id: 'edit',
        title: 'meter.edit',
        icon: PencilLine,
        callback: () => this.openEditModal(),
        disabled: () => !this.cpWriter,
      },
    ];

    if (this.connectionPoint?.address?.darReference) {
      actions.push({
        id: 'toggleMap',
        title: this.mapVisible ? 'connectionpoint.actions.hideMap' : 'connectionpoint.actions.showMap',
        icon: Map,
        callback: () => this.toggleMap(),
      });
    }

    this.headerService.setActions(actions);
  }
}
